// Centralized job type constants & helpers
// NOTE: Keep in sync between frontend and edge functions if duplicated.

export const JobKinds = {
    MissionOrdersBulkPdf: 'mission_orders.bulk_pdf',
    MissionOrdersBulkPdfChunk: 'mission_orders.bulk_pdf_chunk',  // ADD THIS
    MissionOrdersBulkPdfMerge: 'mission_orders.bulk_pdf_merge',  // ADD THIS
    MissionOrdersSinglePdf: 'mission_orders.single_pdf',
    MissionOrdersSingleEmail: 'mission_orders.single_email',
    MatchSheetsBulkEmail: 'match_sheets.bulk_email',
    MessagingBulkEmail: 'messaging.bulk_email',
    DistanceRefresh: 'maintenance.distance_refresh',
    DisciplinaryPvPdf: 'disciplinary.pv_pdf',
    SanctionsPdf: 'sanctions.pdf',
    StatementsSinglePdf: 'statements.single_pdf',
    StatementsBulkPdf: 'statements.bulk_pdf',
    StatementsBulkPdfChunk: 'statements.bulk_pdf_chunk',
    StatementsBulkPdfMerge: 'statements.bulk_pdf_merge',
    CategoryRecapPdf: 'category.recap_pdf',
    CalendarPdf: 'calendar.pdf',
    CalendarOptimize: 'calendar.optimize',
    CalendarDrawGridPdf: 'calendar.draw_grid_pdf',
    CalendarDrawBallotsPdf: 'calendar.draw_ballots_pdf',
    CalendarDrawClubBallotsPdf: 'calendar.draw_club_ballots_pdf',
    CalendarDrawPvPdf: 'calendar.draw_pv_pdf',
    FafAuditFixGoals: 'audit.faf_fix_goals',
    FafExtractAi: 'faf.extract_ai',
    FafSyncMatch: 'faf.sync_match',
    AccountAuditCreate: 'audit.create_accounts',
} as const;

export type JobKind = typeof JobKinds[keyof typeof JobKinds];

// Add payload interfaces for new types
export type MissionOrderJobContext =
    | { context: 'match'; matchId: string; officialId: string }
    | { context: 'mission'; missionAssignmentId: string; missionId?: string };

export interface MissionOrdersBulkPdfChunkPayload extends BaseJobPayload {
    orders: MissionOrderJobContext[];
    fileName: string;
    chunkIndex: number;
    totalChunks: number;
}

export interface MissionOrdersBulkPdfMergePayload extends BaseJobPayload {
    parentJobId: string;
    chunkStoragePaths: string[];
    fileName: string;
}

// Narrow helpers
export const isMissionOrdersPdf = (type: string) => type === JobKinds.MissionOrdersBulkPdf || type === JobKinds.MissionOrdersSinglePdf;
export const isMissionOrdersEmail = (type: string) => type === JobKinds.MissionOrdersSingleEmail;
export const isMatchSheetsBulkEmail = (type: string) => type === JobKinds.MatchSheetsBulkEmail;
export const isMessagingBulkEmail = (type: string) => type === JobKinds.MessagingBulkEmail;
export const isDistanceRefresh = (type: string) => type === JobKinds.DistanceRefresh;

// Minimal payload typing (Edge functions are loosely typed; frontend can create stricter interfaces)
export interface BaseJobPayload { [k: string]: any }

export interface MissionOrdersBulkPdfPayload extends BaseJobPayload {
    orders: MissionOrderJobContext[];
    fileName?: string;
}

export interface MissionOrdersSinglePdfPayload extends BaseJobPayload {
    context?: 'match' | 'mission';
    matchId?: string;
    officialId?: string;
    missionAssignmentId?: string;
    missionId?: string;
    fileName?: string;
    /** Display-only label surfaced in the Job Center scope line. */
    subject?: string;
}

export interface MissionOrdersSingleEmailPayload extends BaseJobPayload {
    context?: 'match' | 'mission';
    matchId?: string;
    officialId?: string;
    missionAssignmentId?: string;
    missionId?: string;
    /** Display-only label surfaced in the Job Center scope line. */
    subject?: string;
}

export interface MatchSheetsBulkEmailPayload extends BaseJobPayload {
    matchIds: string[]; // which match sheets to include
    // Made optional to allow server-side enrichment (legacy callers only had matchIds)
    recipients?: { email: string; name?: string }[];
    /**
     * Dual purpose. Historically the email Subject line; in practice it's
     * always resolved server-side, so callers now repurpose it as the
     * Job Center display label (e.g. "PSG vs OM"). No worker reads it.
     */
    subject?: string;
    message?: string; // plain text or fallback content
    html?: string; // optional HTML version
    attachments?: { filename: string; content: string; type?: string; disposition?: string }[];
}

export interface MessagingBulkEmailPayload extends BaseJobPayload {
    officialIds: string[]; subject: string; message: string;
    // Recipients expanded shape (derived from officialIds) - allow passing fully resolved recipients
    recipients?: { email: string; name?: string; id?: string }[];
}

export interface DistanceRefreshPayload extends BaseJobPayload {
    // Optional filters / limits
    maxMatches?: number; // matchs à venir lus par exécution (≤ 300, par date de match)
    maxUpdates?: number; // écritures max par exécution (≤ 200)
    horizonDays?: number; // matchs d'aujourd'hui à J+horizonDays (défaut 21)
    staleThresholdKm?: number; // écart minimal (≥ 1 km, défaut 5) pour corriger une distance existante
    season?: string; // restrict to a season
}

// PV (procès-verbal) PDF payload for the new generator (v2)
export interface DisciplinaryPvPdfPayload extends BaseJobPayload {
    sessionId: string;
    leagueId: string;
    fileName?: string;
    /** 'pdf' when absent; 'docx' = editable Word PV. */
    format?: 'pdf' | 'docx';
    /** Render even when the inputs match the last generation (« Forcer la génération »). */
    force?: boolean;
}

// Statements (Relevés) PDF payloads
export interface StatementsSinglePdfPayload extends BaseJobPayload {
    officialId: string;
    /**
     * Ligue du relevé (ligue sélectionnée dans l'en-tête). Obligatoire : sans
     * elle can_enqueue_job refuse et le worker échoue (aucun repli « toutes
     * ligues »). Seules les désignations de cette ligue sont lues.
     */
    leagueId: string;
    dateRange: { start: string; end: string };
    fileName?: string;
    /** Display-only label surfaced in the Job Center (e.g. official full name). */
    subject?: string;
}

export interface StatementsBulkPdfPayload extends BaseJobPayload {
    /** Generate statements for all officials in this category */
    category: string;
    /**
     * Ligue du relevé (ligue sélectionnée dans l'en-tête). Obligatoire : sans
     * elle can_enqueue_job refuse et le worker échoue (aucun repli « toutes
     * ligues »). Seules les désignations de cette ligue sont lues.
     */
    leagueId: string;
    dateRange: { start: string; end: string };
    /** Optional explicit officials subset override */
    officialIds?: string[];
    fileName?: string;
    chunkIndex?: number;
    /** Display-only label surfaced in the Job Center scope line. */
    subject?: string;
}

export interface StatementsBulkPdfChunkPayload extends BaseJobPayload {
    parentJobId: string;
    category: string;
    /** Ligue du job parent (déjà vérifiée, revérifiée par le worker de lot). */
    leagueId: string;
    dateRange: { start: string; end: string };
    officialIds: string[];
    fileName: string;
    chunkIndex: number;
    totalChunks: number;
}

export interface StatementsBulkPdfMergePayload extends BaseJobPayload {
    parentJobId: string;
    chunkStoragePaths: string[];
    fileName: string;
}

// Category Recap PDF payload
export interface CategoryRecapPdfPayload extends BaseJobPayload {
    category: string;
    dateRange: { start: string; end: string };
    fileName?: string;
    leagueId?: string;
    includeStadiumAudits?: boolean; // For delegues: whether to include stadium_audit missions
    /** Display-only label surfaced in the Job Center scope line. */
    subject?: string;
}

// Sanctions PDF payload
export interface SanctionsPdfPayload extends BaseJobPayload {
    leagueId: string;
    leagueGroupId?: string;
    leagueGroupName?: string;
    dateRange?: { start: string; end: string };
    /** Journée: championship matches only (a cup match's game_day is almost always 1). */
    gameday?: number;
    /** Cup round (cup_rounds.code, 'none' = cup matches without a round): cup matches only. */
    cupRound?: string;
    /** Display-only round label (Job Center scope line). */
    roundLabel?: string;
    sanctionTypeFilter?: string;
    statusFilter?: 'all' | 'active' | 'cancelled';
    sanctionIds?: string[];
    fileName?: string;
    /** Display-only label surfaced in the Job Center scope line. */
    subject?: string;
}

// Calendar PDF payload — renders a generated/committed group calendar in one of
// three layouts. Pairings are passed in the payload so a not-yet-committed
// preview can be exported directly.
export interface CalendarPdfPairing {
    game_day: number;
    leg: number;
    match_date?: string | null;
    home_team_id: string;
    away_team_id: string;
}
export interface CalendarPdfPayload extends BaseJobPayload {
    leagueGroupId: string;
    groupTitle: string;
    season?: string | null;
    layout: 'rounds' | 'list' | 'matrix';
    /** Render locale: 'fr' (Latin, LTR, default) or 'ar' (Arabic, RTL). */
    locale?: 'fr' | 'ar';
    /** Team label in grid cells: 'abbr' (codes + legend, default) or 'full' (full names, no legend). */
    nameMode?: 'abbr' | 'full';
    legs: number;
    roundsPerLeg: number;
    totalMatches?: number;
    startDate?: string | null;
    endDate?: string | null;
    pairings: CalendarPdfPairing[];
    fileName?: string;
    subject?: string;
}

// Calendar optimizer payload — CP-SAT (OR-Tools) advanced generator run on the
// Cloudflare container. dryRun=true returns a preview (CalendarGenerationResult
// in jobs.result); dryRun=false also commits the optimized pairings.
export interface CalendarOptimizeConstraints {
    /** Payload version. Absent = pre-redesign (v0); the solver's normalize_constraints()
     *  shim maps those forward, so both shapes are accepted during rollout. */
    version?: 1;

    // ── hard ──────────────────────────────────────────────────────────────
    /** CA3 — max consecutive same-venue played games (default 2). */
    maxConsecutive?: number;
    /** CA3 strictness. Absent/false on a v1 payload = soft (the solver reports any
     *  violation instead of going INFEASIBLE); true = hard. v0 payloads are always
     *  hard, for backwards compatibility. */
    maxConsecutiveHard?: boolean;
    /** GA1 — a team forced to a venue and/or opponent on a round. */
    fixed?: Array<{ teamId: string; round?: number; venue?: 'home' | 'away'; opponentId?: string }>;
    /** GA1 — a ground unavailable on a date → the team(s) can't host that round.
     *  Target by teamId (the UI) or stadiumId (→ all teams sharing it). */
    stadiumBlackouts?: Array<{ teamId?: string; stadiumId?: string; date: string }>;
    /** Derbies: tagged pairs. avoidEdges = keep the leg-1 meeting out of the first/last
     *  2 rounds (hard). All tagged pairs are also spread across the leg (soft). */
    derbies?: Array<{ a: string; b: string; avoidEdges?: boolean }>;

    // ── tier 0 guard ──────────────────────────────────────────────────────
    /** Police/security: at most one distinct hosting VENUE per commune per round. */
    sameCityOneHome?: boolean;

    // ── tier 1 objectives ─────────────────────────────────────────────────
    /** Weight breaks in the first/last round-pair by EDGE_MULT (was `openingBalance`). */
    edgeBoost?: boolean;
    /** Equalize break count across teams and, with `direction`, balance each team's
     *  home-breaks vs away-breaks. Dosed by the `breaks` weight. */
    breakFairness?: { direction?: boolean };
    /** Home/away volume. perLeg = leg-1 home count in [min,max] (hard = strictly
     *  enforced); running = cumulative home spread across teams <= band every round. */
    homeBalance?: {
        perLeg?: { min: number; max: number; hard?: boolean };
        running?: { band: number };
    };
    /** Shared strength input (0–100 per team), consumed by topMatches and sequencing. */
    teamStrengths?: Record<string, number>;
    /** Spread the strong-vs-strong "affiches" across the season. Needs teamStrengths. */
    topMatches?: { enabled: true };
    /** Opponent-sequencing fairness. 'lissage' = CA2-style smoothing (objective term);
     *  'renforce' = also picks the lowest Russell carry-over among 3 seeded re-solves. */
    sequencing?: { level: 'lissage' | 'renforce' };

    // ── v0 legacy keys, accepted only by normalize_constraints() ───────────
    /** @deprecated v0 — dropped in v1. */
    noRematchWithin?: number;
    /** @deprecated v0 — folded into `homeBalance.perLeg`. */
    homePerPhase?: { min: number; max: number; hard?: boolean };
    /** @deprecated v0 — folded into `homeBalance.running`. */
    runningHomeFairness?: { band: number };
    /** @deprecated v0 — folded into `sequencing.level`. */
    strengthOfSchedule?: { strengths?: Record<string, number> };
    /** @deprecated v0 — folded into `edgeBoost`. */
    openingBalance?: boolean;
    /** @deprecated v0 — folded into `sequencing.level = 'renforce'`. */
    carryOver?: boolean;
}

/** v1: exactly one weight per editor card. The solver splits a card's weight across
 *  the k terms that implement it (w/k), so a card's priority means one thing. */
export interface CalendarOptimizeWeights {
    breaks?: number;
    homeBalance?: number;
    topMatchSpread?: number;
    sequencing?: number;
    derbySpread?: number;
    travel?: number;

    // ── v0 legacy keys, accepted only by normalize_constraints() ───────────
    /** @deprecated v0 */ travelTotal?: number;
    /** @deprecated v0 */ travelSpread?: number;
    /** @deprecated v0 */ homePerPhase?: number;
    /** @deprecated v0 */ runningHomeFairness?: number;
    /** @deprecated v0 */ strengthOfSchedule?: number;
    /** @deprecated v0 */ openingBalance?: number;
    /** @deprecated v0 */ breakFairness?: number;
}
export interface CalendarOptimizePayload extends BaseJobPayload {
    leagueGroupId: string;
    groupTitle?: string;
    season?: string | null;
    legs: 1 | 2;
    /** Same shape as CalendarOverride (legs/start_date/days_between_rounds/...). */
    override?: Record<string, any>;
    constraints?: CalendarOptimizeConstraints;
    weights?: CalendarOptimizeWeights;
    timeLimitS?: number;
    dryRun: boolean;
    subject?: string;
}

// Calendar draw ceremony PDFs. Both artifacts are keyed ONLY by drawId: the
// worker re-reads the frozen calendar_draws row, so a payload can never smuggle
// in a roster, grid or set of assignments the server did not itself freeze.
// `kind` is carried by jobs.type, not the payload, which is what lets
// finalize_calendar_draw_pdf() decide which artifact column to write.
export interface CalendarDrawPdfPayloadBase extends BaseJobPayload {
    drawId: string;
    /** Render locale: 'fr' (Latin, LTR) or 'ar' (Arabic, RTL). */
    locale?: 'fr' | 'ar';
    fileName?: string;
    /** Display-only label surfaced in the Job Center scope line. */
    subject?: string;
}
/** Pre-publication grid: numbered positions ONLY, never club names. */
export interface CalendarDrawGridPdfPayload extends CalendarDrawPdfPayloadBase { }
/**
 * The number slips the league prints, cuts out and folds into the bowl. Like the
 * grid it carries numbers and league/group chrome only — a slip bearing a club
 * name would tell whoever cuts the sheet what is about to be drawn.
 */
export interface CalendarDrawBallotsPdfPayload extends CalendarDrawPdfPayloadBase { }
/**
 * The club slips of the double tirage: one per club of the FROZEN roster, for
 * the second bowl. These carry names on purpose — they ARE the club bowl — which
 * is safe because the roster is public long before the ceremony; no slip carries
 * a number, so the mapping stays unleakable.
 */
export interface CalendarDrawClubBallotsPdfPayload extends CalendarDrawPdfPayloadBase { }
/** Post-completion procès-verbal: club↔number rows, timestamps, signatures. */
export interface CalendarDrawPvPdfPayload extends CalendarDrawPdfPayloadBase { }

export const isCalendarDrawPdf = (type: string) =>
    type === JobKinds.CalendarDrawGridPdf
    || type === JobKinds.CalendarDrawBallotsPdf
    || type === JobKinds.CalendarDrawClubBallotsPdf
    || type === JobKinds.CalendarDrawPvPdf;

// FAF Audit Fix Goals payload
export interface FafAuditFixGoalsPayload extends BaseJobPayload {
    leagueId: string;
    season: string;
}

// FAF Extract AI payload (single-match AI extraction of referee report events)
export interface FafExtractAiPayload extends BaseJobPayload {
    fafMatchId: string;
    matchId: string | null;
    leagueId: string | null;
    season: string | null;
    force?: boolean;
    homeTeamName?: string;
    awayTeamName?: string;
}

// FAF Sync Match payload (single-match final-report sync: direct or GitHub fallback)
export interface FafSyncMatchPayload extends BaseJobPayload {
    fafMatchId: string;
    matchId: string | null;
    leagueId: string | null;
    season: string | null;
    homeTeamName?: string;
    awayTeamName?: string;
}

// Account Audit & Create payload (no params needed — operates on all officials)
export interface AccountAuditCreatePayload extends BaseJobPayload { }

export type AnyJobPayload = MissionOrdersBulkPdfPayload | MissionOrdersSinglePdfPayload | MissionOrdersSingleEmailPayload | MatchSheetsBulkEmailPayload | MessagingBulkEmailPayload | BaseJobPayload;

// Strict mapping from JobKind to its expected payload shape
export interface JobPayloadMap {
    [JobKinds.MissionOrdersBulkPdf]: MissionOrdersBulkPdfPayload;
    [JobKinds.MissionOrdersBulkPdfChunk]: MissionOrdersBulkPdfChunkPayload;  // ADD
    [JobKinds.MissionOrdersBulkPdfMerge]: MissionOrdersBulkPdfMergePayload;  // ADD
    [JobKinds.MissionOrdersSinglePdf]: MissionOrdersSinglePdfPayload;
    [JobKinds.MissionOrdersSingleEmail]: MissionOrdersSingleEmailPayload;
    [JobKinds.MatchSheetsBulkEmail]: MatchSheetsBulkEmailPayload;
    [JobKinds.MessagingBulkEmail]: MessagingBulkEmailPayload;
    [JobKinds.DistanceRefresh]: DistanceRefreshPayload;
    [JobKinds.DisciplinaryPvPdf]: DisciplinaryPvPdfPayload;
    [JobKinds.SanctionsPdf]: SanctionsPdfPayload;
    [JobKinds.StatementsSinglePdf]: StatementsSinglePdfPayload;
    [JobKinds.StatementsBulkPdf]: StatementsBulkPdfPayload;
    [JobKinds.StatementsBulkPdfChunk]: StatementsBulkPdfChunkPayload;
    [JobKinds.StatementsBulkPdfMerge]: StatementsBulkPdfMergePayload;
    [JobKinds.CategoryRecapPdf]: CategoryRecapPdfPayload;
    [JobKinds.CalendarPdf]: CalendarPdfPayload;
    [JobKinds.CalendarOptimize]: CalendarOptimizePayload;
    [JobKinds.CalendarDrawGridPdf]: CalendarDrawGridPdfPayload;
    [JobKinds.CalendarDrawBallotsPdf]: CalendarDrawBallotsPdfPayload;
    [JobKinds.CalendarDrawClubBallotsPdf]: CalendarDrawClubBallotsPdfPayload;
    [JobKinds.CalendarDrawPvPdf]: CalendarDrawPvPdfPayload;
    [JobKinds.FafAuditFixGoals]: FafAuditFixGoalsPayload;
    [JobKinds.FafExtractAi]: FafExtractAiPayload;
    [JobKinds.FafSyncMatch]: FafSyncMatchPayload;
    [JobKinds.AccountAuditCreate]: AccountAuditCreatePayload;
}

// Helper to derive the payload interface for a given JobKind
export type JobPayloadFor<K extends JobKind> = K extends keyof JobPayloadMap ? JobPayloadMap[K] : BaseJobPayload;
